import { useEffect, useRef, useState, useCallback } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Upload, Save, X, Type, Image as ImageIcon, Zap } from "lucide-react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../config";
import { fetchSingleBlogApi, updateBlogApi } from "./profileAPIs";

export default function BlogForm() {
  const editorRef = useRef<any>(null);
  const navigate = useNavigate();
   const location = useLocation();
  const mentorId = location.state?.mentorId;
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [title, setTitle] = useState("");
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [existingThumbnail, setExistingThumbnail] = useState<string | null>(null);
  const [blogContent, setBlogContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Validation state
  const [titleError, setTitleError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [contentError, setContentError] = useState(false);


  /* ---------- Load blog for edit ---------- */
  useEffect(() => {
    if (!isEdit) return;

    const loadBlog = async () => {
      try {
        const data = await fetchSingleBlogApi(id!) as {
          blog: { title: string; content: string; thumbnailUrl?: string };
        };
        setTitle(data.blog.title);
        setBlogContent(data.blog.content);
        setExistingThumbnail(data.blog.thumbnailUrl || null);
      } catch (error) {
        toast.error("Failed to load blog");
        console.error(error);
      }
    };

    loadBlog();
  }, [id, isEdit]);

  /* ---------- Image Upload for Editor ---------- */
  const uploadImageToServer = useCallback(async (file: File) => {
    setIsUploading(true);
    try {
      if (!file.type.startsWith("image/")) throw new Error("File must be an image");
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read image file"));
        reader.readAsDataURL(file);
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Image upload failed");
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleImageInsert = useCallback(async (file: File) => {
    try {
      const url = await uploadImageToServer(file);
      if (editorRef.current) {
        const imgHtml = `<figure style="margin:1em 0; text-align:center;">
          <img src="${url}" alt="${file.name}" style="max-width:100%; border-radius:8px;" />
        </figure>`;
        editorRef.current.insertContent(imgHtml);
        toast.success("Image inserted");
      }
    } catch (error) {
      console.error(error);
    }
  }, [uploadImageToServer]);

  /* ---------- Quick Actions ---------- */
  const quickActions = [
    { icon: Type, label: "Add Heading", action: () => editorRef.current?.insertContent("<h2>Your Heading</h2>") },
    { icon: ImageIcon, label: "Add Image", action: () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.multiple = true;
        input.onchange = (e) => {
          const files = (e.target as HTMLInputElement).files;
          if (files) for (let i = 0; i < files.length; i++) handleImageInsert(files[i]);
        };
        input.click();
    }},
    { icon: Zap, label: "Add Callout", action: () => editorRef.current?.insertContent('<blockquote><p>💡 Pro Tip: Add your note here...</p></blockquote>') }
  ];

  /* ---------- Submit ---------- */
  const handleSubmit = async () => {
    let hasError = false;

    // Validate title
    if (!title.trim()) {
      setTitleError(true);
      hasError = true;
    } else setTitleError(false);

    // Validate thumbnail
    if (!thumbnail && !existingThumbnail) {
      setThumbnailError(true);
      hasError = true;
    } else setThumbnailError(false);

    // Validate content
    const content = editorRef.current?.getContent();
    if (!content || content === "<p></p>") {
      setContentError(true);
      hasError = true;
    } else setContentError(false);

    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("mentorId", mentorId);
    if (thumbnail) formData.append("thumbnail", thumbnail);

    try {
      setLoading(true);
      if (isEdit) await updateBlogApi(id!, formData);
      else await adminApi.post("/create-blog", formData, { headers: { "Content-Type": "multipart/form-data" }});
      setShowSuccessModal(true);
    } catch (error) {
      toast.error("Failed to save blog");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
      <h1 className="text-3xl font-bold mb-6">{isEdit ? "Edit Blog" : "Create New Blog"}</h1>

      {/* Title */}
      <input
        type="text"
        placeholder="Blog title..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className={`w-full mb-4 p-4 text-xl border rounded-xl focus:outline-none ${titleError ? "border-red-500" : "border-gray-300"}`}
      />
      {titleError && <p className="text-red-500 text-sm mb-2">Please enter a title</p>}

      {/* Thumbnail */}
      <div className="mb-4 relative">
        <label className={`flex items-center gap-3 cursor-pointer ${thumbnailError ? "border border-red-500 p-2 rounded-lg" : ""}`}>
          <Upload className="w-5 h-5 text-purple-600" />
          <span className="text-purple-600 font-medium">
            {thumbnail
              ? thumbnail.name
              : existingThumbnail
              ? "Current: " + existingThumbnail.split("/").pop()
              : "Upload Thumbnail"}
          </span>
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => setThumbnail(e.target.files?.[0] || null)}
          />
        </label>
        {thumbnailError && <p className="text-red-500 text-sm mt-1">Please upload a thumbnail</p>}

        {(thumbnail || existingThumbnail) && (
          <div className="mt-3 relative w-48 h-32">
            <img
              src={thumbnail ? URL.createObjectURL(thumbnail) : existingThumbnail!}
              alt="Thumbnail Preview"
              className="w-full h-full object-cover rounded-xl border"
            />
            <button
              type="button"
              onClick={() => setThumbnail(null)}
              className="absolute top-1 right-1 bg-white rounded-full p-1 shadow hover:bg-gray-100"
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mb-4 flex flex-wrap gap-3">
        {quickActions.map((action, idx) => (
          <button
            key={idx}
            onClick={action.action}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <action.icon className="w-4 h-4" /> {action.label}
          </button>
        ))}
      </div>

      {/* Editor */}
      <Editor
        apiKey="h7pnq72io5ol938d4h0msl4ojca4grmv7orksmbncdhf4xir"
        onInit={(_, editor) => {
          editorRef.current = editor;
          if (isEdit && blogContent) editorRef.current.setContent(blogContent);
        }}
        init={{
          height: 450,
          menubar: true,
          plugins: ["lists", "link", "image", "code", "preview"],
          toolbar:
            "undo redo | bold italic | bullist numlist | link | image | code preview",
          content_style: contentError ? "body { border: 2px solid red; }" : "",
        }}
      />
      {contentError && <p className="text-red-500 text-sm mt-1 mb-2">Please write blog content</p>}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="mt-6 flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl"
      >
        <Save className="w-5 h-5 mr-2" />
        {loading ? "Saving..." : isEdit ? "Update Blog" : "Publish Blog"}
      </button>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-[9999]" style={{ zIndex: 9999 }}>
          <div className="bg-white p-6 rounded-xl shadow-lg text-center">
            <h2 className="text-xl font-bold mb-4">Blog Saved!</h2>
            <p>Your blog has been successfully {isEdit ? "updated" : "published"}.</p>
            <button
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg"
              onClick={() => navigate("/instructor/instructor-profile-blogs", { state: { mentorId } })}
            >
              Go to Blogs
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
